import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import {
  MessageCircle,
  X,
  Send,
  Loader2,
  ChefHat,
  Heart,
  Calendar,
  ExternalLink,
  Sparkles,
  Lock,
  Minimize2,
  Maximize2
} from 'lucide-react';

const MealConcierge = () => {
  const { api, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [isProPlus, setIsProPlus] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!isAuthenticated) {
        setCheckingAccess(false);
        return;
      }
      try {
        const response = await api.get('/subscription/status');
        setIsProPlus(response.data.is_pro && response.data.plan === 'plus');
      } catch (error) {
        console.error('Failed to check subscription:', error);
      } finally {
        setCheckingAccess(false);
      }
    };
    checkSubscription();
  }, [api, isAuthenticated]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && !isMinimized) {
      inputRef.current?.focus();
    }
  }, [isOpen, isMinimized]);

  // Add welcome message when first opened
  useEffect(() => {
    if (isOpen && messages.length === 0 && isProPlus) {
      setMessages([{
        role: 'assistant',
        content: "Hi! I'm your Meal Concierge 👋\n\nI can help you discover recipes, suggest meals based on what's in your fridge, or answer any cooking questions. What are you in the mood for today?",
        recipes: null
      }]);
    }
  }, [isOpen, messages.length, isProPlus]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await api.post('/concierge/chat', {
        message: userMessage,
        session_id: sessionId
      });

      setSessionId(response.data.session_id);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: response.data.message,
        recipes: response.data.recipes
      }]);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Sorry, I had trouble processing that. Please try again.';
      toast.error(errorMsg);
      // Remove the user message if there was an error
      setMessages(prev => prev.slice(0, -1));
      setInput(userMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToMealPlan = async (recipe) => {
    try {
      await api.post(`/concierge/add-to-plan?recipe_id=${recipe.id}&recipe_title=${encodeURIComponent(recipe.title)}&recipe_image=${encodeURIComponent(recipe.image)}`);
      toast.success(`Added "${recipe.title}" to your meal plan!`);
    } catch (error) {
      toast.error('Failed to add to meal plan');
    }
  };

  const handleAddToFavorites = async (recipe) => {
    try {
      await api.post('/favorites', {
        recipe_id: recipe.id,
        recipe_title: recipe.title,
        recipe_image: recipe.image,
        recipe_data: { readyInMinutes: recipe.readyInMinutes }
      });
      toast.success(`Added "${recipe.title}" to favorites!`);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.info('Already in favorites!');
      } else {
        toast.error('Failed to add to favorites');
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Don't render if not authenticated
  if (!isAuthenticated) return null;

  // Floating button when closed
  if (!isOpen) {
    return (
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all z-50 bg-primary hover:bg-primary/90"
        data-testid="concierge-fab"
      >
        <ChefHat className="h-6 w-6" />
      </Button>
    );
  }

  // Minimized state
  if (isMinimized) {
    return (
      <div 
        className="fixed bottom-6 right-6 z-50 cursor-pointer"
        onClick={() => setIsMinimized(false)}
      >
        <Card className="w-72 shadow-lg hover:shadow-xl transition-all border-primary/20">
          <CardContent className="p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ChefHat className="h-5 w-5 text-primary" />
              <span className="font-medium text-sm">Meal Concierge</span>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setIsMinimized(false); }}>
                <Maximize2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={(e) => { e.stopPropagation(); setIsOpen(false); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-[100]" data-testid="concierge-widget">
      <Card className="w-96 h-[500px] shadow-2xl flex flex-col border-primary/20 bg-background">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-primary/10 to-accent/10 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-full bg-primary/20 flex items-center justify-center">
                <ChefHat className="h-5 w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold text-sm">Meal Concierge</h3>
                <p className="text-xs text-muted-foreground">AI-powered cooking assistant</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsMinimized(true)}>
                <Minimize2 className="h-4 w-4" />
              </Button>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setIsOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        {checkingAccess ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !isProPlus ? (
          // Upgrade prompt for non-Pro Plus users
          <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
              <Lock className="h-8 w-8 text-primary/50" />
            </div>
            <h3 className="font-semibold mb-2">Pro Plus Feature</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Get unlimited access to your AI Meal Concierge with Pro Plus. Ask for recipe suggestions, cooking tips, and more!
            </p>
            <Button onClick={() => { setIsOpen(false); navigate('/subscription'); }}>
              <Sparkles className="h-4 w-4 mr-2" />
              Upgrade to Pro Plus
            </Button>
          </div>
        ) : (
          <>
            {/* Messages */}
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((msg, idx) => (
                  <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-1' : ''}`}>
                      <div className={`rounded-2xl px-4 py-2 ${
                        msg.role === 'user' 
                          ? 'bg-primary text-primary-foreground rounded-br-md' 
                          : 'bg-muted rounded-bl-md'
                      }`}>
                        <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      </div>
                      
                      {/* Recipe Cards */}
                      {msg.recipes && msg.recipes.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {msg.recipes.map((recipe, rIdx) => (
                            <Card key={rIdx} className="overflow-hidden">
                              <div className="flex">
                                <img 
                                  src={recipe.image} 
                                  alt={recipe.title}
                                  className="w-20 h-20 object-cover"
                                  onError={(e) => { e.target.src = 'https://via.placeholder.com/80?text=Recipe'; }}
                                />
                                <div className="flex-1 p-2">
                                  <h4 className="font-medium text-sm line-clamp-1">{recipe.title}</h4>
                                  <p className="text-xs text-muted-foreground">{recipe.readyInMinutes} min</p>
                                  <div className="flex gap-1 mt-1">
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 px-2 text-xs"
                                      onClick={() => handleAddToMealPlan(recipe)}
                                    >
                                      <Calendar className="h-3 w-3 mr-1" />
                                      Plan
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 px-2 text-xs"
                                      onClick={() => handleAddToFavorites(recipe)}
                                    >
                                      <Heart className="h-3 w-3 mr-1" />
                                      Save
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="h-7 px-2 text-xs"
                                      onClick={() => navigate(`/recipes/${recipe.id}`)}
                                    >
                                      <ExternalLink className="h-3 w-3 mr-1" />
                                      View
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                
                {/* Loading indicator */}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-sm text-muted-foreground">Thinking...</span>
                      </div>
                    </div>
                  </div>
                )}
                
                <div ref={messagesEndRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t">
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ask me anything about cooking..."
                  disabled={loading}
                  className="flex-1"
                  data-testid="concierge-input"
                />
                <Button 
                  onClick={sendMessage} 
                  disabled={loading || !input.trim()}
                  size="icon"
                  data-testid="concierge-send"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-2 text-center">
                Try: &quot;What can I make with chicken?&quot; or &quot;Quick dinner ideas&quot;
              </p>
            </div>
          </>
        )}
      </Card>
    </div>
  );
};

export default MealConcierge;
